<template>
  <div>
    <!-- Header -->
    <div class="flex items-center justify-between mb-4">
      <div class="flex items-center gap-2">
        <span
          class="px-2 py-1 rounded text-xs font-medium"
          :class="statusClass"
        >
          {{ statusLabel }}
        </span>
        <span v-if="polling" class="text-xs text-gray-500 flex items-center gap-1">
          <svg class="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Checking...
        </span>
      </div>
      <span class="text-xs text-gray-400">{{ formattedDate }}</span>
    </div>

    <!-- Query / Title -->
    <div v-if="isMeetingPrep" class="mb-4">
      <h2 class="sensitive text-base font-semibold text-gray-900" data-demo-blur>{{ job.query }}</h2>
    </div>
    <div v-else class="mb-4 p-3 bg-gray-50 rounded-lg">
      <p class="sensitive text-sm text-gray-700" data-demo-blur>{{ job.query }}</p>
    </div>

    <!-- Loading State -->
    <div v-if="isProcessing" class="text-center py-8">
      <svg class="animate-spin h-8 w-8 mx-auto text-orange-500 mb-3" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-sm text-gray-500">{{ isWeeklyReport ? 'Generating weekly report...' : 'Analyzing your question...' }}</p>
      <p class="text-xs text-gray-400 mt-1">{{ isWeeklyReport ? 'Reports take 10-15 minutes to generate' : 'Queries may take several minutes (5-20 minutes) to complete' }}</p>
    </div>

    <!-- Error State -->
    <div v-else-if="job.status === 'FAILED'" class="p-4 bg-red-50 border border-red-200 rounded-lg">
      <p class="text-sm text-red-700">{{ job.error_message || 'An error occurred while processing your request.' }}</p>
    </div>

    <!-- Success State -->
    <div v-else-if="job.status === 'COMPLETED' && responseData">
      <!-- Out of Scope Warning -->
      <div v-if="isOutOfScope" class="p-5 bg-yellow-50 border border-yellow-300 rounded-lg mb-6">
        <div class="flex gap-3">
          <svg class="w-5 h-5 text-yellow-600 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p class="text-base font-semibold text-yellow-800">Outside current scope</p>
            <p class="text-sm text-yellow-700 mt-2">
              Kairon Intelligence currently analyzes <span class="font-medium">engagements, action items, practice problems, and relationship history</span>.
            </p>
            <p class="text-sm text-yellow-700 mt-1">
              <span class="font-medium">Claims, costs, risk scores, and performance metrics</span> are not yet available.
            </p>
          </div>
        </div>
      </div>

      <!-- Structured Brief Result -->
      <BriefView
        v-if="isBrief"
        :response-data="responseData"
        :regenerating="regenerating"
        @regenerate="$emit('regenerate')"
      />

      <!-- Structured Playbook Result -->
      <PlaybookView
        v-if="isPlaybook"
        :response-data="responseData"
        :regenerating="regenerating"
        @regenerate="$emit('regenerate')"
      />

      <!-- Generic result (personal_analyst, weekly_report) -->
      <template v-if="!isOutOfScope && !isMeetingPrep">
        <!-- Scope Banner -->
        <div class="mb-5 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-start gap-2.5">
            <svg class="w-4 h-4 text-blue-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div class="flex-1">
              <p class="text-sm font-medium text-blue-900 mb-2">Data sources & scope</p>
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <p class="text-xs font-medium text-blue-800 mb-1">Included</p>
                  <ul class="text-xs text-blue-700 space-y-0.5">
                    <li>Engagements & meeting summaries</li>
                    <li>Action items & follow-ups</li>
                    <li>Practice problems & blockers</li>
                    <li>Relationship history & threads</li>
                  </ul>
                </div>
                <div>
                  <p class="text-xs font-medium text-blue-800 mb-1">Not included</p>
                  <ul class="text-xs text-blue-700 space-y-0.5">
                    <li>Claims & clinical data</li>
                    <li>Cost & savings benchmarks</li>
                    <li>Risk scores & HCC data</li>
                    <li>Quality metrics & AWV rates</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Answer -->
        <div class="sensitive mb-6">
          <h3 class="text-sm font-medium text-gray-900 mb-2">Answer</h3>
          <div class="prose prose-sm max-w-none text-gray-700" v-html="renderAnswer(responseData.answer)">
          </div>
        </div>

        <!-- Confidence -->
        <div v-if="responseData.confidence" class="mb-4">
          <span
            class="px-2 py-1 rounded text-xs font-medium"
            :class="confidenceClass"
          >
            {{ responseData.confidence }} confidence
          </span>
        </div>

        <!-- Evidence -->
        <div v-if="responseData.evidence && responseData.evidence.length > 0" class="mb-6">
          <h3 class="text-sm font-medium text-gray-900 mb-2">Evidence</h3>

          <!-- Metric summary cards -->
          <div v-if="metricEvidence.length > 0" class="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
            <div
              v-for="(item, idx) in metricEvidence"
              :key="'m-' + idx"
              class="p-3 bg-gray-50 rounded border border-gray-100 text-center"
            >
              <div class="text-lg font-semibold text-gray-900">{{ formatNumber(item.value) }}</div>
              <div class="text-xs text-gray-500">{{ item.metric }}</div>
            </div>
          </div>

          <!-- Entity ranking list -->
          <div v-if="entityEvidence.length > 0" class="sensitive bg-gray-50 rounded border border-gray-100 mb-3">
            <div
              v-for="(item, idx) in entityEvidence"
              :key="'e-' + idx"
              class="flex items-center justify-between px-3 py-2"
              :class="{ 'border-t border-gray-100': idx > 0 }"
            >
              <div class="flex items-center gap-2">
                <span v-if="item.rank" class="text-xs text-gray-400 w-5 text-right">#{{ item.rank }}</span>
                <span class="sensitive text-sm text-gray-900">{{ item.entity_name || item.name }}</span>
              </div>
              <span class="text-sm font-medium text-gray-600">{{ formatNumber(item.engagement_count || item.count || item.value) }}</span>
            </div>
          </div>

          <!-- Standard engagement evidence -->
          <div v-if="engagementEvidence.length > 0" class="space-y-2">
            <div
              v-for="(item, idx) in engagementEvidence"
              :key="'s-' + idx"
              class="p-3 bg-gray-50 rounded border border-gray-100"
            >
              <div class="flex items-center gap-2 mb-1">
                <span class="sensitive text-xs font-medium text-gray-900">{{ item.entity_name }}</span>
                <span v-if="item.date" class="text-xs text-gray-400">{{ item.date }}</span>
                <span v-if="item.subject" class="text-xs text-gray-500">- {{ item.subject }}</span>
              </div>
              <p v-if="item.relevant_excerpt" class="text-xs text-gray-600 italic">
                "{{ item.relevant_excerpt }}"
              </p>
            </div>
          </div>

          <!-- Generic fallback for unrecognized evidence formats -->
          <div v-if="genericEvidence.length > 0" class="space-y-2">
            <div
              v-for="(item, idx) in genericEvidence"
              :key="'g-' + idx"
              class="p-3 bg-gray-50 rounded border border-gray-100"
            >
              <div class="flex flex-wrap gap-x-4 gap-y-1">
                <div v-for="(val, key) in item" :key="key" class="text-xs">
                  <span class="text-gray-400">{{ key }}:</span>
                  <span class="text-gray-700 font-medium ml-1">{{ val }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <!-- Follow-up Suggestions -->
      <div v-if="responseData.follow_up_suggestions && responseData.follow_up_suggestions.length > 0">
        <h3 class="text-sm font-medium text-gray-900 mb-2">Follow-up questions</h3>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="(suggestion, idx) in responseData.follow_up_suggestions"
            :key="idx"
            class="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-full text-gray-700"
            @click="$emit('follow-up', suggestion)"
          >
            {{ suggestion }}
          </button>
        </div>
      </div>

    </div>

    <!-- Inline annotation floating toolbar -->
    <div
      v-if="selectionToolbar.visible"
      class="fixed z-50 bg-white shadow-lg border border-gray-200 rounded-lg px-2.5 py-1.5 flex items-center gap-1.5"
      :style="{ top: selectionToolbar.y + 'px', left: selectionToolbar.x + 'px' }"
    >
      <span class="text-xs text-gray-400 mr-0.5">Annotate</span>
      <button
        class="p-1.5 rounded-md hover:bg-green-50 text-green-600 transition-colors"
        @click="annotateSelection('useful')"
        title="Useful"
      ><font-awesome-icon :icon="['fas', 'thumbs-up']" class="w-4 h-4" /></button>
      <button
        class="p-1.5 rounded-md hover:bg-amber-50 text-amber-600 transition-colors"
        @click="annotateSelection('partially_useful')"
        title="Partially useful"
      ><font-awesome-icon :icon="['fas', 'circle-minus']" class="w-4 h-4" /></button>
      <button
        class="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition-colors"
        @click="annotateSelection('not_useful')"
        title="Not useful"
      ><font-awesome-icon :icon="['fas', 'thumbs-down']" class="w-4 h-4" /></button>
      <span class="w-px h-4 bg-gray-200"></span>
      <button
        class="p-1.5 rounded-md hover:bg-indigo-50 text-indigo-600 transition-colors"
        @click="annotateSelection('note')"
        title="Add note"
      ><font-awesome-icon :icon="['far', 'note-sticky']" class="w-4 h-4" /></button>
    </div>
  </div>
</template>

<script>
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import BriefView from './BriefView_Original.vue'
import PlaybookView from './PlaybookView.vue'

marked.setOptions({
  breaks: true,
  gfm: true,
})

export default {
  name: 'AnalystResult',
  components: { BriefView, PlaybookView },
  props: {
    job: {
      type: Object,
      required: true,
    },
    polling: {
      type: Boolean,
      default: false,
    },
    regenerating: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['follow-up', 'annotate', 'regenerate'],
  data() {
    return {
      selectionToolbar: { visible: false, x: 0, y: 0, text: '', offset: 0 },
    }
  },
  mounted() {
    document.addEventListener('mouseup', this.handleTextSelection)
    document.addEventListener('mousedown', this.hideToolbar)
  },
  beforeUnmount() {
    document.removeEventListener('mouseup', this.handleTextSelection)
    document.removeEventListener('mousedown', this.hideToolbar)
  },
  methods: {
    handleTextSelection(e) {
      const sel = window.getSelection()
      if (!sel || sel.isCollapsed || !sel.toString().trim()) return

      // Only trigger if selection is within the answer prose area
      const answerEl = this.$el?.querySelector('.prose')
      if (!answerEl) return

      const range = sel.getRangeAt(0)
      if (!answerEl.contains(range.commonAncestorContainer)) return

      const text = sel.toString().trim()
      if (text.length < 5) return // ignore tiny selections

      // Calculate offset within the answer text
      const fullText = answerEl.textContent || ''
      const offset = fullText.indexOf(text)

      const rect = range.getBoundingClientRect()
      this.selectionToolbar = {
        visible: true,
        x: rect.left + (rect.width / 2) - 80,
        y: rect.top - 40,
        text,
        offset: offset >= 0 ? offset : 0,
      }
    },

    hideToolbar(e) {
      // Don't hide if clicking the toolbar itself
      if (e.target.closest && e.target.closest('.z-50')) return
      this.selectionToolbar.visible = false
    },

    annotateSelection(rating) {
      const { text, offset } = this.selectionToolbar
      this.selectionToolbar.visible = false
      window.getSelection()?.removeAllRanges()
      this.$emit('annotate', { text, offset, rating })
    },

    highlightAnchor(anchorText) {
      const proseEl = this.$el?.querySelector('.prose')
      if (!proseEl) return

      // Clean up any previous highlights
      proseEl.querySelectorAll('mark.analyst-highlight').forEach(m => {
        const parent = m.parentNode
        if (parent) {
          parent.replaceChild(document.createTextNode(m.textContent), m)
          parent.normalize()
        }
      })

      const walker = document.createTreeWalker(proseEl, NodeFilter.SHOW_TEXT)
      let found = false

      while (walker.nextNode() && !found) {
        const node = walker.currentNode
        const idx = node.textContent.indexOf(anchorText)
        if (idx === -1) continue

        try {
          const range = document.createRange()
          range.setStart(node, idx)
          range.setEnd(node, Math.min(idx + anchorText.length, node.textContent.length))

          const highlight = document.createElement('mark')
          highlight.className = 'analyst-highlight bg-indigo-100 text-indigo-900 rounded px-0.5 transition-all duration-700'
          range.surroundContents(highlight)
          highlight.scrollIntoView({ behavior: 'smooth', block: 'center' })

          setTimeout(() => {
            if (!highlight.parentNode) return
            highlight.classList.replace('bg-indigo-100', 'bg-transparent')
            setTimeout(() => {
              if (!highlight.parentNode) return
              highlight.parentNode.replaceChild(document.createTextNode(highlight.textContent), highlight)
              highlight.parentNode?.normalize()
            }, 700)
          }, 2000)

          found = true
        } catch {
          // surroundContents fails if range spans multiple elements — fall through to fallback
        }
      }

      if (!found) {
        const allEls = proseEl.querySelectorAll('p, li, td, h1, h2, h3, h4, h5, h6, blockquote')
        for (const el of allEls) {
          if (el.textContent.includes(anchorText.substring(0, 30))) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' })
            el.classList.add('bg-indigo-50', 'rounded')
            setTimeout(() => el.classList.remove('bg-indigo-50'), 2500)
            break
          }
        }
      }
    },

    formatNumber(val) {
      if (val == null) return ''
      if (typeof val === 'number') return val.toLocaleString()
      return val
    },
    renderAnswer(text) {
      if (!text) return ''
      return DOMPurify.sanitize(marked(text))
    },
    isMetricItem(item) {
      return item.metric != null && item.value != null
    },
    isEntityItem(item) {
      return (item.entity_name || item.name) && (item.engagement_count != null || item.count != null) && !item.relevant_excerpt
    },
    isEngagementItem(item) {
      return item.entity_name && (item.relevant_excerpt || item.date)
    },
  },
  computed: {
    isProcessing() {
      return this.job.status === 'QUEUED' || this.job.status === 'PROCESSING'
    },
    responseData() {
      return this.job.response_json
    },
    metricEvidence() {
      if (!this.responseData?.evidence) return []
      return this.responseData.evidence.filter(item => this.isMetricItem(item))
    },
    entityEvidence() {
      if (!this.responseData?.evidence) return []
      return this.responseData.evidence.filter(item => !this.isMetricItem(item) && this.isEntityItem(item))
    },
    engagementEvidence() {
      if (!this.responseData?.evidence) return []
      return this.responseData.evidence.filter(item => !this.isMetricItem(item) && !this.isEntityItem(item) && this.isEngagementItem(item))
    },
    genericEvidence() {
      if (!this.responseData?.evidence) return []
      return this.responseData.evidence.filter(item => !this.isMetricItem(item) && !this.isEntityItem(item) && !this.isEngagementItem(item))
    },
    statusLabel() {
      const labels = {
        QUEUED: 'Queued',
        PROCESSING: 'Analyzing...',
        COMPLETED: 'Complete',
        FAILED: 'Failed',
      }
      return labels[this.job.status] || this.job.status
    },
    statusClass() {
      const classes = {
        QUEUED: 'bg-yellow-100 text-yellow-800',
        PROCESSING: 'bg-blue-100 text-blue-800',
        COMPLETED: 'bg-green-100 text-green-800',
        FAILED: 'bg-red-100 text-red-800',
      }
      return classes[this.job.status] || 'bg-gray-100 text-gray-800'
    },
    confidenceClass() {
      const classes = {
        high: 'bg-green-100 text-green-800',
        medium: 'bg-yellow-100 text-yellow-800',
        low: 'bg-red-100 text-red-800',
      }
      return classes[this.responseData?.confidence] || 'bg-gray-100 text-gray-800'
    },
    isBrief() {
      return this.job.job_type === 'pre_meeting_brief'
    },
    isPlaybook() {
      return this.job.job_type === 'peer_practice_playbook'
    },
    isMeetingPrep() {
      return this.isBrief || this.isPlaybook
    },
    tenantName() {
      return this.$store.getters.current_tenant_name
    },
    isWeeklyReport() {
      return this.job.job_type === 'weekly_report'
    },
    isOutOfScope() {
      if (!this.responseData) return false
      return this.responseData.confidence === 'low' &&
        typeof this.responseData.answer === 'string' &&
        this.responseData.answer.includes('engagement and practice activity analysis only')
    },
    formattedDate() {
      if (!this.job.created_at) return ''
      return new Date(this.job.created_at).toLocaleString()
    },
  },
}
</script>
